import React, { useContext } from 'react'
import { format } from 'date-fns'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useMutation, useQuery } from '@tanstack/react-query'
import Card from '../../../components/Card'
import { api, authHeaders } from '../../../services/api'
import maskUtils from '../../../util/masks'
import Table from '../../../components/Table'
import { FiAlertTriangle, FiArrowRight, FiRepeat, FiUser, FiX } from 'react-icons/fi'
import Spinner from '../../../components/Spinner'
import { GlobalContext } from '../../../contexts/GlobalContext'

const statusNames = {
	pending: 'Pendente',
	authorized: 'Ativa',
	paused: 'Pausada',
	cancelled: 'Cancelada'
}

export default function () {
	const { alterarAssinatura } = useContext(GlobalContext)

	const { data: pessoaModulos, refetch: refetchPessoaModulos } = useQuery({
		queryKey: ['get_hired_modules_on_subscriptions'],
		queryFn: async () => {
			const response = await api.get('pessoa', {
				...authHeaders(),
				params: {
					modulos_contratados: true
				}
			})

			return response
		}
	})

	const { mutate: handleChangeAssinatura } = useMutation({
		mutationFn: async data => {
			await api.put(`assinatura/${data.id}`, data, authHeaders())

			const actions = {
				cancel: 'cancelled'
			}

			alterarAssinatura({
				id: data.id,
				data: {
					status: actions[data.action]
				}
			})

			toast.success('A assinatura foi atualizada.')

			refetchPessoaModulos()
		}
	})

	const modulosTitular = pessoaModulos?.filter(m => m.e_titular)

	const assinaturas = modulosTitular?.reduce((result, pessoaModulo) => {
		const { descricao, plano, status, valor_assinatura, data_proximo_pagamento, assinatura_id, created_at, gratuidade } = pessoaModulo
		const ePlanoGratis = plano.id === 1

		return [
			...result,
			{
				id: assinatura_id,
				plano_descricao: `${plano?.descricao} ${ePlanoGratis ? 'GRATUITO' : plano.periodo}`,
				modulo_descricao: descricao || '-',
				data_assinatura: format(new Date(created_at), 'dd/MM/yyyy'),
				valor: `${maskUtils.maskApply.currency(valor_assinatura / 100)}/${plano.periodo === 'mensal' ? 'mês' : 'ano'}`,
				status,
				status_descricao: statusNames[status],
				gratuidade,
				data_proximo_pagamento: data_proximo_pagamento ? format(new Date(data_proximo_pagamento), 'dd/MM/yyyy') : '-',
				link_mudanca_plano: {
					content: (
						<div className="flex items-center gap-1 justify-center">
							<FiRepeat size={15} />
							<span>Alterar plano</span>
						</div>
					),
					link: `assinaturas/alterar_plano?modulo=${plano.modulo_id}`,
					link_disabled: status !== 'authorized'
				}
			}
		]
	}, [])

	const modulosMembro = pessoaModulos?.filter(m => !m.e_titular)
	
	return (
		<div className="flex flex-col text-slate-800 sm:mx-4 sm:my-6 md:my-12 min-h-[46vh] leading-loose">
			<Card className="flex flex-col">
				<h1 className="text-slate-800 mb-4">Assinaturas</h1>

				{assinaturas === undefined ? (
					<Spinner />
				) : !assinaturas.length ? (
					<div className="flex flex-col items-center gap-6">
						<div className="text-slate-700 bg-yellow-200 w-fit py-2 px-3 rounded-md flex flex-col lg:flex-row justify-between" style={{ fontSize: 13 }}>
							<div className="italic flex items-center gap-1 grid grid-cols-[24px_auto]">
								<FiAlertTriangle size={18} />
								Nenhuma assinatura está ativa no momento.
							</div>
						</div>

						<Link to="/auth/produtos" className="flex gap-1 items-center">
							Conheça nossas soluções
							<FiArrowRight size={16} />
						</Link>

						{!!modulosMembro.length && (
							<>
								<div className="w-full text-slate-500 mx-2 flex flex-col mt-4" style={{ fontSize: 13 }}>
									Detectamos que você já é membro de um ou mais grupos que dão acesso aos seguintes módulos:
									<ul>
										{modulosMembro.map(item => (
											<li key={item.id}>
												- {`${item.descricao} - ${item.plano.descricao}`}
											</li>
										))}
									</ul>

									<span className="mt-4">Para fazer sua própria assinatura, você precisa, primeiramente, sair do grupo ao qual faz parte. Você pode fazer isto facilmente através do gerenciador de grupos.</span>
								</div>

								<Link to="/familia?membro=1" className="text-center group flex gap-1 items-center self-center">
									<span>Vá para o gerenciamento de grupos</span>
									<FiArrowRight size={16} className="group-hover:translate-x-1 transition-all" />
								</Link>
							</>
						)}
					</div>
				) : assinaturas.every(a => a.gratuidade) ? (
					<Table
						headers={[
							{ value: 'Módulo', name: 'modulo_descricao' },
							{ value: 'Plano', name: 'plano_descricao', centered: true },
							{ value: 'Valor', name: 'valor', centered: true },
							{ value: 'Data do próximo faturamento', name: 'data_proximo_pagamento', centered: true, style: { width: 230 }},
							{ value: 'Status', name: 'status_descricao', centered: true },
							{ value: 'Data de adesão', name: 'data_assinatura', centered: true }
						]}
						hideOnSmallHeaders={['data_assinatura', 'data_proximo_pagamento']}
						data={assinaturas}
						style={{ marginTop: 0 }}
					/>
				) : (
					<Table
						headers={[
							{ value: 'Módulo', name: 'modulo_descricao' },
							{ value: 'Plano', name: 'plano_descricao', centered: true },
							{ value: 'Valor', name: 'valor', centered: true },
							{ value: 'Data do próximo faturamento', name: 'data_proximo_pagamento', centered: true, style: { width: 230 }},
							{ value: 'Status', name: 'status_descricao', centered: true },
							{ value: 'Data de adesão', name: 'data_assinatura', centered: true },
							{ 
								value: '', 
								name: 'link_mudanca_plano', 
								link: true, 
								style: { width: 124 } 
							}
						]}
						hideOnSmallHeaders={['data_assinatura', 'data_proximo_pagamento']}
						data={assinaturas}
						style={{ marginTop: 0 }}
						actions={item => {
							const actions = []

							switch (item.status) {
								case 'authorized':
									actions.push({
										action: item => {
											handleChangeAssinatura({
												...item,
												action: 'cancel'
											})
										},
										icon: FiX,
										iconSize: 16,
										name: 'Cancelar assinatura',
										confirmation: 'Você está prestes a <span class="font-bold">cancelar sua assinatura</span>. Isto removerá <span class="font-bold">definitivamente</span> seu acesso ao produto contratado.<br/><br/>Nenhuma cobrança será realizada a partir do cancelamento da assinatura.<br/><br/>Deseja prosseguir com o <span class="font-bold">CANCELAMENTO</span> mesmo assim?',
										checkDisabled: item => item.status === 'cancelled',
										disabledTooltip: 'Não é possível cancelar esta assinatura.'
									})
									break

								default:
									break
							}

							return actions
						}}
					/>
				)}
			</Card>
		</div>
	)
}
